/* eslint-disable max-len */
/* eslint-disable import/prefer-default-export */
import {
  useQueryClient,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  createEstimate,
  createEstimatePhase,
  getEstimate,
  getEstimates,
  getEstimatesList,
  updateEstimate,
  updateEstimatePhase,
  addEstimateNote,
  updateEstimateNote,
  deleteEstimateNote,
  getStatsEstimates,
  createEstimateVarious,
  updateEstimateVarious,
  deleteEstimatePhase,
  deleteEstimateVarious,
  sendEstimate,
  createEstimateVersion,
  duplicateEstimate,
  duplicateEstimatePhase,
  reorderEstimatePhases,
  deleteEstimate,
  updateEstimateTransport,
  updateEstimateSharedWith,
} from '../../api/estimates';
import {
  INote,
  ArgsGetEstimates, IEstimate,
  IPhase,
  IVarious,
  EstimateShareAccess,
} from '~/types/estimates';
import { IMetaPagination } from '~/types/global';

const useGetEstimates = (params: ArgsGetEstimates) => useQuery({
  queryKey: ['estimates', params],
  queryFn: async (): Promise<{ estimates: IEstimate[]; meta?: IMetaPagination } | null> => getEstimates(params),
  keepPreviousData: true,
});

const useGetEstimatesValidations = (params: ArgsGetEstimates) => useQuery({
  queryKey: ['estimates-validations', params],
  queryFn: async (): Promise<{ estimates: IEstimate[]; meta?: IMetaPagination } | null> => getEstimates({
    ...params, status: 'review-pending', search: '', department: '', agency: '',
  }),
  keepPreviousData: true,
});

const useGetStatsEstimates = (params?: { agency?: string; commercialUser?: string; startDate?: string; endDate?: string }) => useQuery({
  queryKey: ['estimates-stats', params],
  queryFn: async (): Promise<any | null> => getStatsEstimates(params),
  keepPreviousData: true,
});

const useGetEstimatesList = () => useQuery({
  queryKey: ['estimates-list'],
  queryFn: async (): Promise<any | null> => getEstimatesList(),
  refetchOnMount: 'always',
});

const useGetEstimate = (id: string) => useQuery({
  queryKey: ['estimate', id],
  queryFn: async (): Promise<IEstimate | null> => getEstimate(id),
  keepPreviousData: false,
});

const useSendEstimate = (successMessage?: string) => {
  const queryEstimate = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sendEstimate(id),
    onSuccess: async ({ estimate }) => {
      queryEstimate.invalidateQueries({ queryKey: ['estimates-stats'] });
      queryEstimate.invalidateQueries({ queryKey: ['estimates-validations'] });
      queryEstimate.invalidateQueries({
        queryKey: ['estimates'],
        exact: false,
      });
      queryEstimate.invalidateQueries({ queryKey: ['client', estimate.client] });
      queryEstimate.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useCreateEstimateVersion = (successMessage?: string) => {
  const queryEstimate = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => createEstimateVersion(id),
    onSuccess: async ({ estimate }) => {
      queryEstimate.invalidateQueries({ queryKey: ['estimates-stats'] });
      queryEstimate.invalidateQueries({ queryKey: ['estimate'] });
      queryEstimate.invalidateQueries({ queryKey: ['estimates-validations'] });
      queryEstimate.invalidateQueries({
        queryKey: ['estimates'],
        exact: false,
      });
      queryEstimate.invalidateQueries({ queryKey: ['client', estimate.client] });
      queryEstimate.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useDuplicateEstimate = (successMessage?: string) => {
  const queryEstimate = useQueryClient();
  return useMutation({
    mutationFn: (data: { estimateId: string; clientId: string; }) => duplicateEstimate(data),
    onSuccess: async ({ estimate }) => {
      queryEstimate.invalidateQueries({ queryKey: ['estimates-stats'] });
      queryEstimate.invalidateQueries({ queryKey: ['estimate'] });
      queryEstimate.invalidateQueries({ queryKey: ['estimates-validations'] });
      queryEstimate.invalidateQueries({
        queryKey: ['estimates'],
        exact: false,
      });
      queryEstimate.invalidateQueries({ queryKey: ['client', estimate.client] });
      queryEstimate.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
      return estimate;
    },
  });
};

const useCreateEstimate = (successMessage?: string) => {
  const queryEstimate = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<IEstimate>) => createEstimate(data),
    onSuccess: async ({ estimate }) => {
      queryEstimate.invalidateQueries({ queryKey: ['estimates-stats'] });
      queryEstimate.invalidateQueries({
        queryKey: ['estimates'],
        exact: false,
      });
      queryEstimate.invalidateQueries({ queryKey: ['client', estimate.client] });
      queryEstimate.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useUpdateEstimate = (successMessage?: string) => {
  const queryEstimate = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<IEstimate>) => updateEstimate(data),
    onSuccess: async ({ estimate }) => {
      queryEstimate.invalidateQueries({ queryKey: ['estimates-stats'] });

      queryEstimate.invalidateQueries({
        queryKey: ['estimates'],
        exact: false,
      });
      queryEstimate.invalidateQueries({ queryKey: ['client', estimate.client._id] });
      queryEstimate.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useCreateEstimatePhase = (successMessage?: string) => {
  const queryEstimate = useQueryClient();
  return useMutation({
    mutationFn: (data: { estimateId: string, type: string }) => createEstimatePhase(data),
    onSuccess: async ({ estimate }: any) => {
      queryEstimate.invalidateQueries({
        queryKey: ['estimates'],
        exact: false,
      });
      queryEstimate.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useDeleteEstimatePhase = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { phaseId: string, estimateId: string }) => deleteEstimatePhase(data),
    onSuccess: async ({ estimate }) => {
      queryClient.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useDuplicateEstimatePhase = (successMessage?: string) => {
  const queryEstimate = useQueryClient();
  return useMutation({
    mutationFn: (data: { phaseId: string, estimateId: string }) => duplicateEstimatePhase(data),
    onSuccess: async ({ estimate }) => {
      queryEstimate.invalidateQueries({ queryKey: ['estimates-stats'] });
      queryEstimate.invalidateQueries({ queryKey: ['estimates-validations'] });
      queryEstimate.invalidateQueries({
        queryKey: ['estimates'],
        exact: false,
      });
      queryEstimate.invalidateQueries({ queryKey: ['client', estimate.client] });
      queryEstimate.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useReorderEstimatePhases = () => {
  const queryEstimate = useQueryClient();
  return useMutation({
    mutationFn: (data: { estimateId: string; phaseIds: string[] }) => reorderEstimatePhases(data),
    onSuccess: async ({ estimate }) => {
      queryEstimate.setQueryData(['estimate', estimate._id], estimate);
    },
    onError: () => {
      toast.error('Impossible de réorganiser les phases');
    },
  });
};

const useCreateEstimateVarious = (successMessage?: string) => {
  const queryEstimate = useQueryClient();
  return useMutation({
    mutationFn: (estimateId: string) => createEstimateVarious(estimateId),
    onSuccess: async ({ estimate }) => {
      queryEstimate.invalidateQueries({
        queryKey: ['estimates'],
        exact: false,
      });
      queryEstimate.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useUpdateEstimateVarious = (successMessage?: string) => {
  const queryEstimate = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<IVarious> & { estimateId : string }) => updateEstimateVarious(data),
    onSuccess: async ({ estimate }) => {
      queryEstimate.invalidateQueries({
        queryKey: ['estimates'],
        exact: false,
      });
      queryEstimate.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useDeleteEstimateVarious = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { variousId: string, estimateId: string }) => deleteEstimateVarious(data),
    onSuccess: async ({ estimate }) => {
      queryClient.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useUpdateEstimatePhase = (successMessage?: string) => {
  const queryEstimate = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<IPhase> & { estimateId : string }) => updateEstimatePhase(data),
    onSuccess: async ({ estimate }) => {
      queryEstimate.invalidateQueries({
        queryKey: ['estimates'],
        exact: false,
      });
      queryEstimate.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useAddEstimateNote = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<INote> & { documentId: string }) => addEstimateNote(data),
    onSuccess: async ({ estimate }) => {
      queryClient.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useUpdateEstimateNote = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<INote> & { documentId: string }) => updateEstimateNote(data),
    onSuccess: async ({ estimate }) => {
      queryClient.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useDeleteEstimateNote = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { noteId: string, estimateId: string }) => deleteEstimateNote(data),
    onSuccess: async ({ estimate }) => {
      queryClient.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useUpdateEstimateTransport = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { estimateId: string }) => updateEstimateTransport(data),
    onSuccess: async ({ estimate }) => {
      queryClient.setQueryData(['estimate', estimate._id], estimate);
      if (successMessage) toast(successMessage);
    },
  });
};

const useDeleteEstimate = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { estimateId: string }) => deleteEstimate(data),
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ['estimates'],
        exact: false,
      });
      if (successMessage) toast(successMessage);
    },
  });
};

const useUpdateEstimateSharedWith = (successMessage?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      estimateId: string,
      sharedWith: { user: string, access: EstimateShareAccess }[],
    }) => updateEstimateSharedWith(data),
    onSuccess: async ({ estimate }) => {
      queryClient.setQueryData(['estimate', estimate._id], estimate);
      queryClient.invalidateQueries({
        queryKey: ['estimates'],
        exact: false,
      });
      if (successMessage) toast(successMessage);
    },
  });
};

export {
  useGetEstimate,
  useGetEstimatesValidations,
  useSendEstimate,
  useGetStatsEstimates,
  useGetEstimates,
  useGetEstimatesList,
  useCreateEstimate,
  useUpdateEstimate,
  useDeleteEstimate,
  useCreateEstimateVersion,
  useUpdateEstimateTransport,
  useDuplicateEstimate,
  useCreateEstimatePhase,
  useUpdateEstimatePhase,
  useDuplicateEstimatePhase,
  useReorderEstimatePhases,
  useDeleteEstimatePhase,
  useCreateEstimateVarious,
  useUpdateEstimateVarious,
  useDeleteEstimateVarious,
  useAddEstimateNote,
  useUpdateEstimateNote,
  useDeleteEstimateNote,
  useUpdateEstimateSharedWith,
};
